import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Snackbar, Alert, Box, CircularProgress, Typography, Paper, Grid } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { Person, Email, Phone, LocationOn, Home as HomeIcon, Business, LocationCity, Public, MarkunreadMailbox } from '@mui/icons-material';
import { useAuth } from '../../../shared/contexts/AuthContext';

export const Profile: React.FC = () => {
    const { user: contextUser, updateProfile, syncProfile } = useAuth();
    const [user, setUser] = useState<any>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    // Derived properties
    const isAdmin = useMemo(() => {
        const u = user || contextUser;
        return u && (u.role === 'admin' || u.role === 'super_admin');
    }, [user, contextUser]);

    // Initialize state from context
    useEffect(() => {
        if (contextUser && Object.keys(contextUser).length > 0) {
            if (!user) {
                const userData = { ...contextUser };
                if (userData.role === 'admin' || userData.role === 'super_admin') {
                    userData.address = userData.address || {};
                }
                setUser(userData);
            }
        } else if (!isFetching) {
            const fetchProfile = async () => {
                try {
                    setIsFetching(true);
                    await syncProfile();
                } catch (error) {
                    console.error('Error syncing profile:', error);
                } finally {
                    setIsFetching(false);
                }
            };
            fetchProfile();
        }
    }, [contextUser, isFetching, syncProfile]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;

        setIsUpdating(true);
        const profileData: any = {
            name: user.name,
            email: user.email,
        };

        if (isAdmin) {
            profileData.latitude = user.latitude || null;
            profileData.longitude = user.longitude || null;
            profileData.address = {
                houseNoAndStreetName: user.address?.houseNoAndStreetName || '',
                landmark: user.address?.landmark || '',
                city: user.address?.city || '',
                state: user.address?.state || '',
                country: user.address?.country || '',
                pincode: user.address?.pincode || ''
            };
        } else {
            profileData.mobile_number = user.mobile || user.mobile_number;
            profileData.address = user.address || '';
        }

        try {
            const response = await updateProfile(profileData);

            if (response?.success) {
                const updatedUser = response?.data?.admin || response?.data?.user || response?.data || response;
                if (updatedUser) {
                    setUser({ ...updatedUser, address: updatedUser.address || (isAdmin ? {} : '') });
                }
                setSnackbar({ open: true, message: 'Profile updated successfully!', severity: 'success' });
            } else {
                setSnackbar({
                    open: true,
                    message: response?.message || 'Failed to update profile.',
                    severity: 'error'
                });
            }
        } catch (error: any) {
            console.error('Error updating profile:', error);
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || 'Error updating profile.',
                severity: 'error'
            });
        } finally {
            setIsUpdating(false);
        }
    };

    // If we don't have user data yet, show the loader. 
    // This prevents the "Could not load profile" flicker when switching tabs.
    if (!user) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh" gap={2}>
                <CircularProgress size={60} thickness={4} sx={{ color: '#3b82f6' }} />
                <Typography variant="h6" color="textSecondary">Loading profile...</Typography>
            </Box>
        );
    }

    return (
        <Box className="relative z-10 w-full max-w-4xl mx-auto my-10 px-4">
            <Paper elevation={0} className="backdrop-blur-xl bg-white/80 rounded-[32px] border border-white/50 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.1)] p-8 md:p-12 overflow-hidden relative">
                <Box className="flex flex-col lg:flex-row gap-12 relative z-10">
                    {/* Left Side: Profile Intro */}
                    <Box className="flex flex-col items-center lg:w-1/3">
                        <Box className="relative">
                            <Box className="absolute inset-0 bg-blue-500 rounded-full blur-2xl opacity-10"></Box>
                            <img
                                className="w-40 h-40 rounded-full border-8 border-white shadow-2xl object-cover relative z-10"
                                src="/assets/images/user.png"
                                alt="User"
                                onError={(e) => { (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=' + (user?.name || 'User'); }}
                            />
                        </Box>
                        <Box className="mt-8 text-center">
                            <Typography variant="h4" className="font-extrabold text-gray-900 tracking-tight">
                                {user?.name}
                            </Typography>
                            <Typography className="text-gray-500 font-medium mt-1">
                                {user?.role?.replace('_', ' ').toUpperCase()}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right Side: Form Fields */}
                    <form onSubmit={handleSubmit} className="flex-1 space-y-8">
                        <Box className="space-y-6">
                            <Typography variant="h5" className="font-bold text-gray-800 border-b pb-4 border-gray-100">
                                Personal Details
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField label="Full Name" value={user.name || ''} onChange={(e) => setUser({ ...user, name: e.target.value })} fullWidth variant="outlined" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField label="Email Address" value={user.email || ''} onChange={(e) => setUser({ ...user, email: e.target.value })} fullWidth variant="outlined" InputProps={{ readOnly: true }} />
                                </Grid>
                            </Grid>
                        </Box>

                        {isAdmin ? (
                            <Box className="space-y-6">
                                <Typography variant="h5" className="font-bold text-gray-800 border-b pb-4 border-gray-100 flex items-center gap-2">
                                    <HomeIcon className="text-blue-500" /> Address & Location
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField label="House No / Street" value={user.address?.houseNoAndStreetName || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, houseNoAndStreetName: e.target.value } })} fullWidth />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField label="Landmark" value={user.address?.landmark || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, landmark: e.target.value } })} fullWidth />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField label="City" value={user.address?.city || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, city: e.target.value } })} fullWidth />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField label="State" value={user.address?.state || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, state: e.target.value } })} fullWidth />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField label="Pincode" value={user.address?.pincode || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, pincode: e.target.value } })} fullWidth />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField label="Country" value={user.address?.country || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, country: e.target.value } })} fullWidth />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography className="font-bold text-gray-700 mb-2">Geolocation (Coordinates)</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Latitude"
                                            type="number"
                                            value={user.latitude || ''}
                                            onChange={(e) => setUser({ ...user, latitude: e.target.value })}
                                            fullWidth
                                            placeholder="e.g. 23.0225"
                                            InputProps={{ startAdornment: <LocationOn className="mr-2 text-blue-500" /> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Longitude"
                                            type="number"
                                            value={user.longitude || ''}
                                            onChange={(e) => setUser({ ...user, longitude: e.target.value })}
                                            fullWidth
                                            placeholder="e.g. 72.5714"
                                            InputProps={{ startAdornment: <LocationOn className="mr-2 text-blue-500" /> }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <Box className="space-y-6">
                                <Typography variant="h5" className="font-bold text-gray-800 border-b pb-4 border-gray-100">
                                    Contact Info
                                </Typography>
                                <TextField label="Mobile Number" value={user.mobile || ''} onChange={(e) => setUser({ ...user, mobile: e.target.value })} fullWidth InputProps={{ startAdornment: <Phone className="mr-2 text-gray-400" /> }} />
                                <TextField label="Full Address" value={user.address || ''} onChange={(e) => setUser({ ...user, address: e.target.value })} multiline rows={4} fullWidth />
                            </Box>
                        )}

                        <Box className="flex justify-end pt-8">
                            <AppButton type="submit" variant="primary" loading={isUpdating} sx={{ px: 8, py: 1.5, borderRadius: '12px', fontSize: '1.1rem' }}>
                                Update Profile
                            </AppButton>
                        </Box>
                    </form>
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px', minWidth: '300px', boxShadow: '0 8px 16px rgba(0,0,0,0.15)' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};
